import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CustomerCombobox } from "@/components/customers/CustomerCombobox";
import { QuickReplies } from "@/components/messages/QuickReplies";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MessageSquare, Send, Search, Image as ImageIcon, Check, CheckCheck, AlertTriangle, Plus, X, Trash2, MoreVertical } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GSM7 = new Set("@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà^{}\\[~]|€");
export const segments = (body) => {
  const uni = [...(body || "")].some((ch) => !GSM7.has(ch));
  const n = (body || "").length;
  const count = uni ? (n <= 70 ? 1 : Math.ceil(n / 67)) : (n <= 160 ? 1 : Math.ceil(n / 153));
  return { chars: n, encoding: uni ? "unicode" : "gsm", segments: n ? count : 0 };
};
export const fmtTime = (iso) => iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";

const isAdmin = () => { try { return JSON.parse(localStorage.getItem("user") || "{}").role === "admin"; } catch { return false; } };

export const DeleteConversationDialog = ({ target, onClose, onDeleted }) => {
  const [busy, setBusy] = useState(false);
  if (!target) return null;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/sms/conversations/${target.phone}`);
      toast.success(`Conversation deleted (${data.messages} message${data.messages === 1 ? "" : "s"})`);
      onDeleted(target.phone);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete conversation"); } finally { setBusy(false); onClose(); }
  };
  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="delete-conversation-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete conversation with {target.label}?</AlertDialogTitle>
          <AlertDialogDescription>This permanently deletes {target.count} message{target.count === 1 ? "" : "s"} with this number. Opt-out status is kept.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-conversation-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={run} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="delete-conversation-confirm">{busy ? "Deleting…" : "Delete conversation"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const StatusTick = ({ m }) => {
  if (m.direction !== "outbound") return null;
  if (m.status === "failed" || m.status === "undelivered") return <span className="text-red-200 text-[10px]" title={m.error || m.status} data-testid="sms-status-failed">✕ {m.status}</span>;
  if (m.status === "delivered") return <CheckCheck className="w-3 h-3 inline" data-testid="sms-status-delivered" />;
  if (m.status === "queued") return <span className="text-[10px]" title={`Waits for quiet hours to end (${fmtTime(m.send_after)})`} data-testid="sms-status-queued">⏱ queued</span>;
  return <Check className="w-3 h-3 inline" data-testid="sms-status-sent" />;
};

export const Composer = ({ onSend, disabled, placeholder = "Type a message…", phone }) => {
  const [body, setBody] = useState("");
  const [media, setMedia] = useState("");
  const [busy, setBusy] = useState(false);
  const seg = segments(body);
  const submit = async () => {
    if (!body.trim() && !media.trim()) return;
    setBusy(true);
    try { await onSend(body.trim(), media.trim() ? [media.trim()] : []); setBody(""); setMedia(""); } finally { setBusy(false); }
  };
  return (
    <div className="border-t border-slate-200 p-3 space-y-2 bg-white" data-testid="sms-composer">
      {media && <div className="flex items-center gap-2 text-xs text-slate-600"><ImageIcon className="w-3 h-3" /><span className="truncate flex-1">{media}</span><button onClick={() => setMedia("")} data-testid="sms-media-clear"><X className="w-3 h-3" /></button></div>}
      <div className="flex gap-2 items-end">
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2} disabled={disabled} placeholder={placeholder} className="flex-1 rounded-xl border border-slate-300 p-2 text-sm resize-none" data-testid="sms-composer-input"
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }} />
        <QuickReplies phone={phone} disabled={disabled} onPick={(text) => setBody(text)} />
        <Button variant="outline" size="icon" title="Attach image URL (MMS)" disabled={disabled} onClick={() => { const u = window.prompt("Image URL to attach (MMS):", media); if (u !== null) setMedia(u); }} data-testid="sms-attach-button"><ImageIcon className="w-4 h-4" /></Button>
        <Button onClick={submit} disabled={disabled || busy || (!body.trim() && !media)} className="bg-blue-700 hover:bg-blue-800" data-testid="sms-send-button"><Send className="w-4 h-4" /></Button>
      </div>
      <p className="text-[11px] text-slate-500" data-testid="sms-segment-counter">{seg.chars} chars · {seg.segments} segment{seg.segments === 1 ? "" : "s"} ({seg.encoding === "gsm" ? "GSM 160" : "Unicode 70"}){media ? " · MMS" : ""}</p>
    </div>
  );
};

export const Thread = ({ phone, onSent, onDeleteConversation, onConversationRemoved }) => {
  const [data, setData] = useState(null);
  const [deleteMsg, setDeleteMsg] = useState(null);
  const admin = isAdmin();
  const bottom = useRef(null);
  const load = useCallback(() => axios.get(`${API}/sms/conversations/${phone}/messages`).then((r) => setData(r.data)).catch(() => {}), [phone]);
  useEffect(() => { setData(null); load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);
  const send = async (body, media_urls) => {
    try {
      await axios.post(`${API}/sms/send`, { phone, customer_id: data?.customer?.id, body, media_urls });
      await load(); onSent && onSent();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
  };
  const removeMessage = async () => {
    const m = deleteMsg;
    setDeleteMsg(null);
    try {
      const { data: r } = await axios.delete(`${API}/sms/messages/${m.id}`);
      toast.success("Message deleted");
      if (r.conversation_removed) { onConversationRemoved && onConversationRemoved(phone); return; }
      await load(); onSent && onSent();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete message"); }
  };
  if (!data) return <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">Loading…</div>;
  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="sms-thread">
      <div className="px-4 py-3 border-b border-slate-200 bg-white flex items-center justify-between">
        <div><p className="font-semibold text-slate-800" data-testid="sms-thread-name">{data.customer?.name || data.phone_pretty}</p><p className="text-xs text-slate-500">{data.phone_pretty}{data.customer ? (data.customer.sms_opt_in ? " · marketing opt-in" : " · no marketing consent") : " · not a customer"}</p></div>
        {admin && <Button variant="ghost" size="icon" className="text-red-600 hover:bg-red-50" title="Delete conversation" onClick={() => onDeleteConversation({ phone, label: data.customer?.name || data.phone_pretty, count: data.messages.length })} data-testid="sms-thread-delete-button"><Trash2 className="w-4 h-4" /></Button>}
      </div>
      {data.customer?.sms_opt_out && <div className="bg-red-50 border-b border-red-200 text-red-800 text-sm px-4 py-2 flex items-center gap-2" data-testid="sms-opted-out-banner"><AlertTriangle className="w-4 h-4" /> This customer replied STOP — texts are blocked until they reply START.</div>}
      <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50">
        {data.messages.length === 0 && <p className="text-center text-sm text-slate-400 mt-10">No messages yet.</p>}
        {data.messages.map((m) => (
          <div key={m.id} className={`group flex items-center gap-1 ${m.direction === "outbound" ? "justify-end" : "justify-start"}`} data-testid={`sms-bubble-${m.direction}`}>
            {admin && m.direction === "outbound" && <BubbleMenu onDelete={() => setDeleteMsg(m)} />}
            <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm shadow-sm ${m.direction === "outbound" ? "bg-blue-700 text-white rounded-br-sm" : "bg-white text-slate-800 rounded-bl-sm border border-slate-200"}`}>
              {(m.media_urls || []).map((u) => <a key={u} href={u} target="_blank" rel="noreferrer" className="block mb-1"><img src={u} alt="MMS" className="rounded-lg max-h-48" /></a>)}
              <p className="whitespace-pre-wrap break-words">{m.body}</p>
              <p className={`text-[10px] mt-1 flex items-center gap-1 ${m.direction === "outbound" ? "text-blue-100 justify-end" : "text-slate-400"}`}>{fmtTime(m.created_at)} <StatusTick m={m} />{m.campaign_id && <Badge className="bg-purple-100 text-purple-700 text-[9px] py-0 hover:bg-purple-100">campaign</Badge>}</p>
            </div>
            {admin && m.direction !== "outbound" && <BubbleMenu onDelete={() => setDeleteMsg(m)} />}
          </div>
        ))}
        <div ref={bottom} />
      </div>
      <Composer onSend={send} disabled={!!data.customer?.sms_opt_out} phone={phone} />
      {deleteMsg && (
        <AlertDialog open onOpenChange={(o) => !o && setDeleteMsg(null)}>
          <AlertDialogContent data-testid="delete-message-dialog">
            <AlertDialogHeader><AlertDialogTitle>Delete this message?</AlertDialogTitle><AlertDialogDescription>"{(deleteMsg.body || "[MMS]").slice(0, 120)}" will be removed from TrackApp only — it cannot be unsent from the customer's phone.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel data-testid="delete-message-cancel">Cancel</AlertDialogCancel><AlertDialogAction onClick={removeMessage} className="bg-red-600 hover:bg-red-700" data-testid="delete-message-confirm">Delete message</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};

const BubbleMenu = ({ onDelete }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild><button className="opacity-40 group-hover:opacity-100 focus:opacity-100 text-slate-500 hover:text-slate-800 p-1 rounded" title="Message options" data-testid="sms-bubble-menu"><MoreVertical className="w-4 h-4" /></button></DropdownMenuTrigger>
    <DropdownMenuContent align="end"><DropdownMenuItem onClick={onDelete} className="text-red-600" data-testid="sms-bubble-delete"><Trash2 className="w-4 h-4 mr-2" />Delete message</DropdownMenuItem></DropdownMenuContent>
  </DropdownMenu>
);

export const NewMessageDialog = ({ onClose, onPick }) => {
  const [customerId, setCustomerId] = useState("");
  const [phone, setPhone] = useState("");
  const [customers, setCustomers] = useState([]);
  useEffect(() => { axios.get(`${API}/customers`).then((r) => setCustomers(r.data)).catch(() => {}); }, []);
  const go = async () => {
    if (customerId) {
      const { data } = await axios.get(`${API}/sms/customers/${customerId}/phone`);
      if (!data.phone) return toast.error("This customer has no valid mobile number");
      onPick(data.phone);
    } else {
      const digits = phone.replace(/\D/g, "");
      const p = digits.length === 10 ? "1" + digits : digits;
      if (p.length !== 11) return toast.error("Enter a valid US number");
      onPick(p);
    }
    onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sms-new-dialog">
        <DialogHeader><DialogTitle>New text message</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><p className="text-xs text-slate-500 mb-1">Pick a customer</p><CustomerCombobox customers={customers} value={customerId} onChange={setCustomerId} /></div>
          <p className="text-center text-xs text-slate-400">— or —</p>
          <Input placeholder="Phone number, e.g. (305) 555-0100" value={phone} onChange={(e) => { setPhone(e.target.value); setCustomerId(""); }} data-testid="sms-new-phone-input" />
          <Button onClick={go} className="w-full bg-blue-700 hover:bg-blue-800" data-testid="sms-new-start-button">Open conversation</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const Inbox = ({ initialPhone }) => {
  const [convs, setConvs] = useState([]);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(initialPhone || null);
  const [newOpen, setNewOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const admin = isAdmin();
  const afterDeleted = (phone) => { if (active === phone) setActive(null); load(); };
  const load = useCallback(() => axios.get(`${API}/sms/conversations`, { params: { search: search || undefined } }).then((r) => setConvs(r.data.conversations)).catch(() => {}), [search]);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
  useEffect(() => { if (initialPhone) setActive(initialPhone); }, [initialPhone]);
  return (
    <div className="flex h-[calc(100vh-180px)] min-h-[480px] rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-lg" data-testid="sms-inbox">
      <aside className={`w-full md:w-80 border-r border-slate-200 flex flex-col ${active ? "hidden md:flex" : "flex"}`}>
        <div className="p-3 border-b border-slate-200 space-y-2">
          <Button onClick={() => setNewOpen(true)} className="w-full bg-blue-700 hover:bg-blue-800" data-testid="sms-new-message-button"><Plus className="w-4 h-4 mr-1" />New message</Button>
          <div className="relative"><Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" /><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, number, text" className="pl-8" data-testid="sms-search-input" /></div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {convs.length === 0 && <p className="text-sm text-slate-400 p-4 text-center" data-testid="sms-conversations-empty">No conversations yet.</p>}
          {convs.map((c) => (
            <div key={c.phone} className={`flex items-start border-b border-slate-100 hover:bg-slate-50 ${active === c.phone ? "bg-blue-50" : ""}`} data-testid="sms-conversation-row">
              <button onClick={() => setActive(c.phone)} className="flex-1 min-w-0 text-left px-3 py-3" data-testid="sms-conversation-open">
                <div className="flex items-center justify-between"><p className="font-medium text-slate-800 truncate">{c.customer_name || c.phone_pretty}</p>{c.unread_count > 0 && <Badge className="bg-blue-600 text-white hover:bg-blue-600" data-testid="sms-unread-badge">{c.unread_count}</Badge>}</div>
                <p className="text-xs text-slate-500 truncate">{c.last_message}</p>
                <p className="text-[10px] text-slate-400">{fmtTime(c.last_at)}{c.opted_out ? " · opted out" : ""}</p>
              </button>
              {admin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><button className="p-2 mt-2 mr-1 text-slate-400 hover:text-slate-700 rounded" title="Conversation options" data-testid="sms-conversation-menu"><MoreVertical className="w-4 h-4" /></button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end"><DropdownMenuItem className="text-red-600" onClick={async () => { let count = 0; try { const { data } = await axios.get(`${API}/sms/conversations/${c.phone}/messages`); count = data.messages.length; } catch { /* ignore */ } setDeleteTarget({ phone: c.phone, label: c.customer_name || c.phone_pretty, count }); }} data-testid="sms-conversation-delete"><Trash2 className="w-4 h-4 mr-2" />Delete conversation</DropdownMenuItem></DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}
        </div>
      </aside>
      <section className={`flex-1 flex flex-col min-w-0 ${active ? "flex" : "hidden md:flex"}`}>
        {active ? <><button className="md:hidden text-xs text-blue-700 px-4 pt-2 text-left" onClick={() => setActive(null)} data-testid="sms-back-button">← Conversations</button><Thread phone={active} onSent={load} onDeleteConversation={setDeleteTarget} onConversationRemoved={afterDeleted} /></> : <div className="flex-1 flex flex-col items-center justify-center text-slate-400"><MessageSquare className="w-12 h-12 mb-2" /><p className="text-sm">Select a conversation or start a new message</p></div>}
      </section>
      {newOpen && <NewMessageDialog onClose={() => setNewOpen(false)} onPick={(p) => { setActive(p); load(); }} />}
      <DeleteConversationDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={afterDeleted} />
    </div>
  );
};
